<script setup>
import { computed, inject } from 'vue'
import SubscriptionService from '@/services/SubscriptionService'
import { useStore } from 'vuex'
import _ from 'lodash'
import FormButton from '../../components/Atoms/FormButton.vue'
import Tooltip from '../Atoms/Tooltip.vue'
import Badge from '../../components/Badge.vue'
import { openCalendly } from '@/utils'
import { track, EVENTS } from '@/lib/tracking'

const props = defineProps({
  plan: {
    type: Object,
  },
  isYearly: {
    type: Boolean
  },
  isCart: {
    type: Boolean
  },
  productType: {
    type: String
  }
})

const store = useStore()

const { updateSelectedPaymentMethod, selectPackage } = inject('selectedPaymentMethod')

// Computed
const addonSubscriptions = computed(() => store.getters['User/addonSubscriptions'])

const price = computed(() => props.isYearly ? props.plan.info?.yearly : props.plan.baseCharge)

const isEventsPlan = computed(() => props.plan?.info?.name === 'Events')
const reserveBadgeRow = computed(() => !props.isCart && props.productType !== 'ott')

const labelComputed = computed(() => {
  if (props.plan?.name === 'Custom') return 'Contact Us'
  return isCurrentSubscription.value ? 'Active plan' : 'Subscribe Now'
})

const shouldShowBookMeetingButton = (plan) => {
  if (plan?.info?.name === 'Events') return true
  if (plan?.info?.name === 'Ultra') return true
  return false
}
const isCurrentSubscription = computed(() => {
  const subscription = props.productType === 'ott'
    ? addonSubscriptions.value.find(subscription => subscription.category === 'ott' && subscription.enabled)
    : addonSubscriptions.value.find(subscription => subscription.category === 'live' && subscription.enabled)
  return (subscription?.package?._id === props.plan?._id) && props.plan.name !== 'Custom'
})

const isDisabled = computed(() => isCurrentSubscription.value)
// Methods
const getChangeDirection = (pack) => {
  const currentPackage = store.getters['User/baseSubscription']?.package
  if (!currentPackage) return 'new'
  const toMonthly = (p) => (p?.cycleAge === 365 ? (p?.baseCharge || 0) / 12 : (p?.baseCharge || 0))
  const next = toMonthly(pack)
  const current = toMonthly(currentPackage)
  if (next > current) return 'upgrade'
  if (next < current) return 'downgrade'
  return 'same'
}

const selectSubscriptionPackage = async (pack) => {
  selectPackage(pack)
  const checkpaymentMethods = await SubscriptionService.getUserPaymentMethods()
  updateSelectedPaymentMethod(checkpaymentMethods[0])
  track(EVENTS.PLAN_SELECTED, {
    packageId: pack._id,
    planName: pack.info?.name || pack.name,
    planCategory: pack.category,
    billingCycle: pack.cycleAge === 365 ? 'yearly' : 'monthly',
    baseCharge: pack.baseCharge,
    changeDirection: getChangeDirection(pack),
  })
}
</script>

<template>
  <header class="!px-4 !py-5 flex flex-col relative bg-surface-2 rounded-t-xl overflow-hidden"
    :class="[isCart ? '' : (productType === 'ott' ? 'h-[120px]' : 'h-[256px] lg:h-[268px]')]">
    <h6 v-if="isEventsPlan" class="text-white text-xs bg-main py-1 absolute top-0 right-0 w-full text-center">Branded Membership Site</h6>
    <h2 class="text-lg font-bold !mt-3 text-white" :class="reserveBadgeRow ? '!mb-1' : '!mb-3'">
      {{ props.plan?.info?.name ? props.plan?.info?.name :
      props.plan?.name }} 
      <Badge v-if="isEventsPlan && !reserveBadgeRow" pill inline type="purple-dark">OTT Apps</Badge>
      <Badge v-if="props.plan?.info?.popular && isCart" type="blue" pill inline
      title="POPULAR" />
    </h2>
    <!-- Same height in every column so prices and actions line up across plans. -->
    <div v-if="reserveBadgeRow" class="h-5 mb-2 flex items-center" data-testid="plan-badge-row">
      <Badge v-if="isEventsPlan" pill inline type="purple-dark">OTT Apps</Badge>
    </div>
    <p v-if="props.plan.baseCharge && productType !== 'ott'" class="flex items-end gap-1 my-2">
      <span class="text-2xl font-semibold leading-none text-white">
        ${{ price ? price : props.plan?.baseCharge }}
      </span>
      <span class="text-sm leading-4 text-surface-7">
        /month
      </span>
    </p>
    <div v-if="props.plan.baseCharge && productType !== 'ott'" class="min-h-[2.25rem]">
      <template v-if="props.isYearly">
        <p class="text-tiny text-surface-7">Billed ${{ props.plan.baseCharge }}/year</p>
        <p class="text-tiny font-semibold text-gold">Save ${{ props.plan.info?.saved }}</p>
      </template>
    </div>
    <div v-if="!isCart" class="mt-auto flex flex-col gap-1.5 pt-4">
      <FormButton v-if="props.plan?.name === 'Custom' && productType !== 'ott'"
        class="justify-center"
        :customClasses="[isDisabled ? '!border-surface-6 text-surface-6 hover:text-surface-6 pointer-events-none' : '!border-main text-main hover:!border-main-light hover:text-main-light', 'h-9 flex items-center justify-center rounded-md text-tiny !border hover:no-underline']"
        :disabled="isDisabled" full-width type='main-outline' :label="labelComputed"
        @click="openCalendly()" />
      <FormButton v-else-if="productType !== 'ott'" :disabled="isDisabled" full-width
        :type="props.plan?.info?.popular ? 'main' : 'secondary'"
        class="justify-center shrink-0" :label="labelComputed"
        @click="selectSubscriptionPackage(props.plan)" />
      <FormButton v-else full-width class="justify-center shrink-0"
        label="Contact Us" @click="openCalendly()" />
      <div class="h-5 flex items-center justify-center">
        <FormButton v-if="shouldShowBookMeetingButton(props.plan)" @click="openCalendly()" type="link"
          class="!text-main hover:!text-main-light text-center w-full justify-center" label="Book a Demo" />
      </div>
    </div>
  </header>
</template>

<style></style>
